import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import useSWR from "swr";
import { IdCard, Minus, Plus, Truck } from "lucide-react";
import { Seo } from "@/components/Seo";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { ProductGrid, StrainBadge } from "@/components/ProductCard";
import { useCart } from "@/context/Providers";
import { defaultOptions, fetcher, money, stripHtml, swrOpts, unitPrice, variantFor } from "@/lib/api";
import { abs, breadcrumbLd } from "@/lib/seo";
import NotFound from "@/pages/NotFound";

const productLd = (p, url) => {
  const mods = p.options.flatMap((o) => o.choices.map((c) => c.modifier));
  const high = p.price + (p.options.length ? Math.max(...mods, 0) : 0);
  const availability = `https://schema.org/${p.in_stock ? "InStock" : "OutOfStock"}`;
  return {
    "@context": "https://schema.org", "@type": "Product", name: p.name, sku: p.sku, image: p.images, url: abs(url),
    description: stripHtml(p.description).slice(0, 500) || p.name, category: p.categories[0]?.name,
    ...(p.brand ? { brand: { "@type": "Brand", name: p.brand } } : {}),
    offers: high > p.price
      ? { "@type": "AggregateOffer", priceCurrency: "USD", lowPrice: p.price, highPrice: high, availability, offerCount: mods.length }
      : { "@type": "Offer", priceCurrency: "USD", price: p.price, availability, url: abs(url) },
  };
};

const Gallery = ({ p }) => {
  const [i, setI] = useState(0);
  return (
    <div>
      <div className="aspect-square overflow-hidden rounded-3xl border border-line bg-white">
        {p.images[i] && <img src={p.images[i]} alt={`${p.name}${p.brand ? ` by ${p.brand}` : ""} — ${p.categories[0]?.name || "cannabis product"}`} width="800" height="800" fetchPriority="high" className="h-full w-full object-contain p-6" data-testid="product-main-image" onError={(e) => { e.currentTarget.src = "/brand/shadc-logo-512.webp"; }} />}
      </div>
      {p.images.length > 1 && (
        <div className="mt-3 flex gap-3">
          {p.images.map((src, k) => (
            <button key={src} type="button" onClick={() => setI(k)} aria-label={`Show image ${k + 1}`} aria-pressed={i === k}
              className={`h-20 w-20 overflow-hidden rounded-xl border bg-white ${i === k ? "border-gold" : "border-line"}`} data-testid={`product-thumb-${k}`}>
              <img src={src} alt="" width="80" height="80" loading="lazy" className="h-full w-full object-contain p-1" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const Buy = ({ p }) => {
  const { add } = useCart();
  const [opts, setOpts] = useState(() => defaultOptions(p));
  const [qty, setQty] = useState(1);
  useEffect(() => { setOpts(defaultOptions(p)); setQty(1); }, [p]);
  const unit = unitPrice(p, opts);
  const variant = variantFor(p, opts);
  const stock = p.variants?.length ? Boolean(variant?.in_stock) : p.in_stock;
  const compare = variant ? (variant.compare_price > variant.price ? variant.compare_price : null) : p.compare_price ? p.compare_price + (unit - p.price) : null;
  return (
    <div className="mt-6 space-y-6">
      <p className="flex items-baseline gap-3">
        <span className="text-3xl font-bold text-white" data-testid="product-price">{money(unit)}</span>
        {compare && <s className="text-lg text-gray-500" data-testid="product-compare-price">{money(compare)}</s>}
        {compare && <span className="rounded-full bg-gold px-2.5 py-1 text-xs font-bold uppercase text-ink">Save {Math.round((1 - unit / compare) * 100)}%</span>}
      </p>
      <p className={`flex items-center gap-2 text-sm ${stock ? "text-jade-soft" : "text-gray-500"}`} data-testid="product-availability">
        <span className={`h-2 w-2 rounded-full ${stock ? "bg-jade" : "bg-gray-600"}`} aria-hidden />{stock ? "In stock — available for delivery" : p.in_stock ? "This option is sold out — try another" : "Currently sold out"}
      </p>
      {p.options.map((o) => (
        <fieldset key={o.name}>
          <legend className="mb-3 text-xs font-bold uppercase tracking-[0.18em] text-gray-400">{o.name}: <span className="text-white normal-case tracking-normal">{opts[o.name]}</span></legend>
          <div className="flex flex-wrap gap-2" role="radiogroup">
            {o.choices.map((c) => {
              const on = opts[o.name] === c.text;
              return (
                <button key={c.text} type="button" role="radio" aria-checked={on} onClick={() => setOpts({ ...opts, [o.name]: c.text })}
                  className={`min-h-[44px] rounded-full border px-4 text-sm transition-colors ${on ? "border-gold bg-gold/10 text-gold" : "border-line text-gray-300 hover:border-gray-500"} ${c.available === false ? "line-through opacity-50" : ""}`}
                  aria-label={c.available === false ? `${c.text} (sold out)` : c.text}
                  data-testid={`product-option-${c.text.toLowerCase().replace(/\W+/g, "-")}`}>{c.text}{c.modifier > 0 && <span className="ml-1 text-xs text-gray-500">+{money(c.modifier)}</span>}</button>
              );
            })}
          </div>
        </fieldset>
      ))}
      <div className="flex gap-3">
        <div className="flex items-center rounded-full border border-line">
          <button type="button" onClick={() => setQty(Math.max(1, qty - 1))} aria-label="Decrease quantity" className="grid h-12 w-12 place-items-center hover:text-gold" data-testid="product-qty-decrease"><Minus className="h-4 w-4" /></button>
          <span className="w-8 text-center font-semibold" aria-live="polite" data-testid="product-qty">{qty}</span>
          <button type="button" onClick={() => setQty(Math.min(99, qty + 1))} aria-label="Increase quantity" className="grid h-12 w-12 place-items-center hover:text-gold" data-testid="product-qty-increase"><Plus className="h-4 w-4" /></button>
        </div>
        <button type="button" disabled={!stock} onClick={() => add(p, opts, qty)} className="btn-gold flex-1" data-testid="product-add-to-cart-button">
          {stock ? `Add to cart · ${money(unit * qty)}` : "Sold out"}
        </button>
      </div>
      <ul className="space-y-2 rounded-2xl border border-line bg-panel p-5 text-sm text-gray-300">
        <li className="flex gap-3"><Truck className="h-4 w-4 shrink-0 text-jade" aria-hidden />Free delivery on orders over $50 across Washington, DC. <Link to="/delivery/" className="link-gold">Details</Link></li>
        <li className="flex gap-3"><IdCard className="h-4 w-4 shrink-0 text-jade" aria-hidden />Valid 21+ photo ID required at delivery.</li>
      </ul>
    </div>
  );
};

export default function Product() {
  const { slug } = useParams();
  const { data: p, error } = useSWR(`/products/${slug}`, fetcher, { ...swrOpts, keepPreviousData: false });
  if (error?.response?.status === 404) return <NotFound />;
  if (!p) return <div className="container-x grid gap-10 py-12 lg:grid-cols-2" aria-busy="true"><div className="aspect-square animate-pulse rounded-3xl bg-panel" /><div className="h-96 animate-pulse rounded-3xl bg-panel" /></div>;

  const url = `/shop/product/${p.slug}/`;
  const cat = p.categories[0];
  const crumbs = [{ name: "Home", path: "/" }, { name: "Shop", path: "/shop/" }, ...(cat ? [{ name: cat.name, path: cat.slug === "deals" ? "/deals/" : `/shop/${cat.slug}/` }] : []), { name: p.name, path: url }];
  const details = [["SKU", p.sku], ["Category", p.categories.map((c) => c.name).join(", ")], ["Brand", p.brand], ["Strain type", p.strain],
    ...Object.entries(p.attributes).filter(([k]) => !["Strain Type", "Strain", "Type"].includes(k))].filter(([, v]) => v);
  const desc = stripHtml(p.description);

  return (
    <>
      <Seo title={p.seo_title || `${p.name}${cat ? ` | ${cat.name}` : ""} | SHADC Dispensary DC`}
        description={p.seo_description || (desc ? desc.slice(0, 155) : `${p.name} — order for delivery in Washington, DC from SHADC Dispensary. Adults 21+.`)}
        path={url} image={p.image} type="product" jsonLd={[productLd(p, url), breadcrumbLd(crumbs)]} />
      <div className="container-x py-8 sm:py-12">
        <Breadcrumbs items={crumbs} />
        <div className="mt-8 grid gap-10 lg:grid-cols-2 lg:gap-16">
          <Gallery p={p} />
          <div>
            <div className="flex flex-wrap items-center gap-2 text-sm text-gray-400">
              {cat && <Link to={crumbs[2].path} className="hover:text-gold">{cat.name}</Link>}{p.brand && <span>· {p.brand}</span>}<StrainBadge strain={p.strain} />
            </div>
            <h1 className="mt-3 text-3xl font-bold leading-tight text-white sm:text-4xl" data-testid="product-title">{p.name}</h1>
            <Buy p={p} />
          </div>
        </div>
        <div className="mt-16 grid gap-10 lg:grid-cols-[1.6fr_1fr]">
          <section aria-labelledby="desc-title">
            <h2 id="desc-title" className="text-2xl font-semibold text-white">Product description</h2>
            {p.description ? <div className="rich mt-4" dangerouslySetInnerHTML={{ __html: p.description }} data-testid="product-description" /> : <p className="mt-4 text-gray-400">Description coming soon.</p>}
          </section>
          <section aria-labelledby="details-title" className="card-surface h-fit p-6">
            <h2 id="details-title" className="font-sans text-xs font-bold uppercase tracking-[0.18em] text-gold">Details</h2>
            <dl className="mt-4 divide-y divide-line text-sm">
              {details.map(([k, v]) => <div key={k} className="flex justify-between gap-4 py-3"><dt className="text-gray-400">{k}</dt><dd className="text-right text-white">{v}</dd></div>)}
            </dl>
          </section>
        </div>
        {p.related.length > 0 && (
          <section className="mt-20" aria-labelledby="related-title">
            <div className="mb-8 flex items-end justify-between">
              <h2 id="related-title" className="text-3xl font-semibold text-white">You may also like</h2>
              {cat && <Link to={crumbs[2].path} className="link-gold text-sm font-semibold">More {cat.name} →</Link>}
            </div>
            <ProductGrid items={p.related.slice(0, 4)} />
          </section>
        )}
      </div>
    </>
  );
}
